/**
 * Client Compliance Types - TASK-228
 * Shared Data Model & ComplianceContext
 *
 * Single source of truth for all compliance-related client data
 * enabling shared data across forms (KYC, Risk Profile, Client Profile, Form A)
 * with multi-source ingestion support.
 */

import type { Address } from './kyc.types';

// ============================================================================
// DATA SOURCE TYPES
// ============================================================================

/**
 * Types of data sources for tracked fields
 * Extensible for multiple ingestion sources
 */
export type DataSourceType =
  | 'document'       // Extracted from uploaded documents (passport, statements)
  | 'transcription'  // Extracted from meeting transcriptions
  | 'api'            // Fetched from external APIs
  | 'manual'         // Manually entered by user
  | 'inherited';     // Legacy data from existing system

/**
 * Data source metadata for field tracking
 */
export interface DataSource {
  type: DataSourceType;
  sourceId: string;           // Unique identifier (upload_id, meeting_id, etc.)
  sourceName: string;         // Human-readable name (e.g., "passport.pdf")
  extractedAt: string;        // ISO timestamp
  extractedBy?: string;       // User or system that extracted
  confidence: number;         // 0-1 confidence score
  metadata?: Record<string, unknown>; // Additional source-specific data
}

/**
 * Source-specific interfaces for type-safe metadata
 */
export interface DocumentSource extends DataSource {
  type: 'document';
  metadata?: {
    documentType?: string;
    pageNumber?: number;
    fieldLocation?: { x: number; y: number; width: number; height: number };
    ocrConfidence?: number;
  };
}

export interface TranscriptionSource extends DataSource {
  type: 'transcription';
  metadata?: {
    meetingId?: string;
    timestamp?: string;
    speaker?: string;
    utteranceId?: string;
  };
}

export interface APISource extends DataSource {
  type: 'api';
  metadata?: {
    endpoint?: string;
    responseCode?: number;
    apiVersion?: string;
  };
}

// ============================================================================
// TRACKED FIELD WRAPPER
// ============================================================================

/**
 * Generic wrapper for fields that tracks value, confidence, source, and history
 */
export interface TrackedField<T> {
  value: T;
  confidence: number;
  source: DataSource;
  history?: DataSource[];      // Previous sources for audit trail
  modifiedAt?: string;
  modifiedBy?: string;
  isVerified?: boolean;        // Manually verified by user
  verifiedBy?: string;
  verifiedAt?: string;
}

// ============================================================================
// SHARED DATA INTERFACES (Canonical Field Names)
// ============================================================================

/**
 * Shared Personal Data
 * Canonical names: firstName, lastName, dateOfBirth, nationalities, etc.
 */
export interface SharedPersonalData {
  [key: string]: unknown;
  firstName?: TrackedField<string>;
  middleName?: TrackedField<string>;
  lastName?: TrackedField<string>;
  dateOfBirth?: TrackedField<string>;
  placeOfBirth?: TrackedField<string>;
  gender?: TrackedField<'male' | 'female' | 'other' | 'prefer_not_to_say'>;
  maritalStatus?: TrackedField<'single' | 'married' | 'divorced' | 'widowed' | 'separated' | 'domestic_partnership'>;
  nationalities?: TrackedField<string[]>;
  languages?: TrackedField<string[]>;
  passportNumber?: TrackedField<string>;
  nationalId?: TrackedField<string>;
  // TASK-321: SSN field for personal identification
  ssn?: TrackedField<string>;
}

/**
 * Shared Contact Data
 * Canonical names: phonePrimary, emailPrimary, residentialAddress, etc.
 */
export interface SharedContactData {
  [key: string]: unknown;
  phonePrimary?: TrackedField<string>;
  phoneSecondary?: TrackedField<string>;
  emailPrimary?: TrackedField<string>;
  emailSecondary?: TrackedField<string>;
  residentialAddress?: TrackedField<Address>;
  mailingAddress?: TrackedField<Address>;
  preferredContactMethod?: TrackedField<'email' | 'phone' | 'whatsapp' | 'video_call' | 'in_person'>;
  // TASK-320: Relationship fields for ContactInfoStep
  relationshipReason?: TrackedField<string>;
  relationshipPurpose?: TrackedField<string>;
}

/**
 * Shared Employment Data
 * Canonical names: occupation, employerName, employmentStatus, etc.
 */
export interface SharedEmploymentData {
  [key: string]: unknown;
  employmentStatus?: TrackedField<'employed' | 'selfEmployed' | 'unemployed' | 'retired' | 'student' | 'other'>;
  occupation?: TrackedField<string>;
  employerName?: TrackedField<string>;
  employmentIndustry?: TrackedField<string>;
  yearsEmployed?: TrackedField<number>;
  workAddress?: TrackedField<Address>;
}

/**
 * Shared Financial Data
 * Canonical names: totalAssets, annualIncome, sourceOfWealth, etc.
 */
export interface SharedFinancialData {
  [key: string]: unknown;
  totalAssets?: TrackedField<number>;
  liquidAssets?: TrackedField<number>;
  annualIncome?: TrackedField<number>;
  totalLiabilities?: TrackedField<number>;
  netWorth?: TrackedField<number>;
  sourceOfWealth?: TrackedField<string>;
  primaryWealthSource?: TrackedField<string>;
  assetCurrency?: TrackedField<string>;
}

/**
 * Shared Tax Data
 * Canonical names: taxResidence, usPerson, fatcaStatus, etc.
 */
export interface SharedTaxData {
  [key: string]: unknown;
  taxResidence?: TrackedField<string>;
  taxId?: TrackedField<string>;
  usPerson?: TrackedField<boolean>;
  fatcaStatus?: TrackedField<'compliant' | 'non_compliant' | 'pending' | 'exempt'>;
  taxJurisdictions?: TrackedField<string[]>;
  // TASK-321: Extended tax fields
  usSSN?: TrackedField<string>;
  taxExempt?: TrackedField<boolean>;
  taxExemptReason?: TrackedField<string>;
  additionalTaxResidencies?: TrackedField<Array<{country: string; taxId?: string}>>;
}

/**
 * Shared PEP Data
 * Canonical names: isPep, pepType, pepPosition, etc.
 */
export interface SharedPEPData {
  [key: string]: unknown;
  isPep?: TrackedField<boolean>;
  isRca?: TrackedField<boolean>;
  pepType?: TrackedField<'domestic' | 'foreign' | 'international'>;
  pepPosition?: TrackedField<string>;
  pepCountry?: TrackedField<string>;
  pepStartDate?: TrackedField<string>;
  pepEndDate?: TrackedField<string>;
  rcaRelationship?: TrackedField<string>;
  rcaPepName?: TrackedField<string>;
}

/**
 * Shared Adverse Media Screening Data
 * TASK-321: Screening results and board positions
 */
export interface SharedAdverseMediaData {
  hasAdverseMedia?: TrackedField<boolean>;
  adverseMediaType?: TrackedField<string[]>;
  adverseMediaDetails?: TrackedField<string>;
  sanctionsScreened?: TrackedField<boolean>;
  sanctionsHit?: TrackedField<boolean>;
  sanctionsDetails?: TrackedField<string>;
  boardPositions?: TrackedField<Array<{
    company: string;
    position: string;
    country: string;
    current: boolean;
    isPubliclyTraded?: boolean;
    isin?: string;
  }>>;
  // TASK-322: Risk assessment fields
  reputationalRisk?: TrackedField<string>;
  insiderFlag?: TrackedField<boolean>;
}

/**
 * Beneficial Owner Data (shared between Client Profile and Form A)
 * Canonical names: ownerName, ownerDob, ownerNationality, etc.
 */
export interface BeneficialOwnerData {
  ownerName?: TrackedField<string>;
  ownerDob?: TrackedField<string>;
  ownerNationality?: TrackedField<string>;
  ownerDomicile?: TrackedField<string>;
  ownerPercentage?: TrackedField<number>;
  ownerIsPep?: TrackedField<boolean>;
  controlType?: TrackedField<'direct' | 'indirect' | 'control_person'>;
  profession?: TrackedField<string>;
  sourceOfWealth?: TrackedField<string>;
}

// ============================================================================
// TASK-261: NEW SHARED DATA INTERFACES
// ============================================================================

/**
 * Shared Identification Data
 * TASK-261: For passport, ID card, and AHV number
 */
export interface SharedIdentificationData {
  passportNumber?: TrackedField<string>;
  passportExpiry?: TrackedField<string>;
  passportCountry?: TrackedField<string>;
  idCardNumber?: TrackedField<string>;
  ahvNumber?: TrackedField<string>;
}

/**
 * Shared Personal Background Data
 * TASK-261: Education, family, and professional background
 */
export interface SharedPersonalBackgroundData {
  educationLevel?: TrackedField<string>;
  institutionsAttended?: TrackedField<string[]>;
  professionalCertifications?: TrackedField<string[]>;
  hobbiesInterests?: TrackedField<string[]>;
  familyComposition?: TrackedField<string>;
  dependentsCount?: TrackedField<number>;
  spouseEmploymentStatus?: TrackedField<string>;
}

/**
 * Shared Wealth Summary Data
 * TASK-261: Profile-level wealth fields
 */
export interface SharedWealthSummaryData {
  netWorth?: TrackedField<number>;
  liquidNetWorth?: TrackedField<number>;
  netWorthCurrency?: TrackedField<string>;
  sourceOfWealth?: TrackedField<string>;
  sourceOfFunds?: TrackedField<string>;
  primaryWealthSource?: TrackedField<string>;
  wealthNarrative?: TrackedField<string>;
}

/**
 * Shared Asset Breakdown Data
 * TASK-261: Asset allocation percentages
 */
export interface SharedAssetBreakdownData {
  cash?: TrackedField<number>;
  securities?: TrackedField<number>;
  real_estate?: TrackedField<number>;
  business_equity?: TrackedField<number>;
  commodities?: TrackedField<number>;
  crypto?: TrackedField<number>;
  other?: TrackedField<number>;
}

/**
 * Shared Additional Wealth Info Data
 * TASK-261: Inheritance and unusual transactions
 */
export interface SharedAdditionalWealthInfoData {
  inheritanceExpected?: TrackedField<boolean>;
  inheritanceDetails?: TrackedField<string>;
  unusualTransactions?: TrackedField<boolean>;
  unusualTransactionDetails?: TrackedField<string>;
  sourceDocumentation?: TrackedField<string>;
}

/**
 * Shared Income Summary Data
 * TASK-322: Top-level income fields from IncomeSourceStep
 */
export interface SharedIncomeSummaryData {
  totalAnnualIncome?: TrackedField<number>;
  incomeCurrency?: TrackedField<string>;
  primaryIncomeType?: TrackedField<string>;
}

/**
 * Shared Income Expectations Data
 * TASK-261: Future income expectations
 */
export interface SharedIncomeExpectationsData {
  expectedIncomeChange?: TrackedField<boolean>;
  expectedChangeDetails?: TrackedField<string>;
  retirementIncome?: TrackedField<boolean>;
  retirementIncomeDetails?: TrackedField<string>;
  otherFinancialSupport?: TrackedField<boolean>;
  otherSupportDetails?: TrackedField<string>;
}

/**
 * Shared Wealth Source Data (array item)
 * TASK-261: Single wealth source entry
 */
export interface SharedWealthSourceData {
  sourceType?: TrackedField<string>;
  sourceDescription?: TrackedField<string>;
  percentage?: TrackedField<number>;
  documented?: TrackedField<boolean>;
  acquisitionPeriod?: TrackedField<string>;
  // TASK-320: Extended timing fields
  acquisitionStartDate?: TrackedField<string>;
  acquisitionEndDate?: TrackedField<string>;
  isOngoing?: TrackedField<boolean>;
  // Asset details
  assetType?: TrackedField<string>;
  assetValue?: TrackedField<number>;
  assetCurrency?: TrackedField<string>;
  assetLocation?: TrackedField<string>;
  // Verification
  verificationNotes?: TrackedField<string>;
  supportingDocumentTypes?: TrackedField<string[]>;
  // Liquidity & Risk
  liquidityLevel?: TrackedField<string>;
  riskLevel?: TrackedField<string>;
}

/**
 * Shared Income Source Data (array item)
 * TASK-261: Single income source entry
 */
export interface SharedIncomeSourceData {
  incomeType?: TrackedField<string>;
  incomeDescription?: TrackedField<string>;
  annualAmount?: TrackedField<number>;
  currency?: TrackedField<string>;
  frequency?: TrackedField<string>;
  sourceName?: TrackedField<string>;
  sourceCountry?: TrackedField<string>;
  // TASK-320: Extended date/status fields
  startDate?: TrackedField<string>;
  isOngoing?: TrackedField<boolean>;
  // Verification
  verified?: TrackedField<boolean>;
  supportingDocuments?: TrackedField<string[]>;
  // Tax status
  taxPaid?: TrackedField<boolean>;
  taxJurisdiction?: TrackedField<string>;
  taxRate?: TrackedField<number>;
  // Stability
  stabilityRating?: TrackedField<string>;
  expectedContinuity?: TrackedField<string>;
}

/**
 * Shared Business Activity Data (array item)
 * TASK-261: Single business activity entry
 */
export interface SharedBusinessActivityData {
  businessName?: TrackedField<string>;
  businessType?: TrackedField<string>;
  registrationNumber?: TrackedField<string>;
  registrationCountry?: TrackedField<string>;
  primaryIndustry?: TrackedField<string>;
  ownershipPercentage?: TrackedField<number>;
  ownershipType?: TrackedField<string>;
  yearsInBusiness?: TrackedField<number>;
  annualRevenue?: TrackedField<number>;
  cashIntensive?: TrackedField<boolean>;
  // TASK-321: Extended business activity fields
  industryRiskLevel?: TrackedField<string>;
  numberOfEmployees?: TrackedField<number>;
  revenueCurrency?: TrackedField<string>;
  crossBorderTransactions?: TrackedField<boolean>;
  highRiskCountries?: TrackedField<string[]>;
}

// ============================================================================
// STATUS & SCREENING TYPES
// ============================================================================

/**
 * Form completion status
 */
export interface FormStatus {
  status: 'not_started' | 'in_progress' | 'submitted' | 'completed' | 'expired' | 'rejected' | 'changes_requested';
  completedSections: number;
  totalSections: number;
  lastUpdated?: string;
  completedAt?: string;
  completedBy?: string;
  expiresAt?: string;
  // TASK-230: Additional metadata fields
  version?: number;                  // Form version number
  nextReviewDate?: string;           // ISO date for next required review
  completedSectionNames?: string[];  // Names of completed sections for visual chips
  /**
   * TASK-828: the SUITABILITY state, which is not the same fact as form completion.
   *
   * `string` — a determined risk category is on file.
   * `null`   — there is none, so no suitability assessment can be made against this record
   *            however many sections are filled. This is what the mandatory `reprofile_due`
   *            alert fires on, and it is why a page reading "Completed" could sit beside an
   *            alert saying "incomplete" with both being honestly derived.
   * `undefined` — the server's summary could not be read. Deliberately distinct from `null`:
   *            "we do not know" must never render as "there is none".
   */
  determinedRiskProfile?: string | null;
}

/**
 * Screening status (PEP, Sanctions, Adverse Media)
 */
export interface ScreeningStatus {
  type: 'pep' | 'sanctions' | 'adverseMedia';
  status: 'pending' | 'clear' | 'hit' | 'review_required' | 'false_positive';
  checkedAt?: string;
  checkedBy?: string;
  nextCheckDue?: string;
  provider?: string;
  referenceId?: string;
  details?: string;
}

/**
 * Risk factor for risk rating calculation
 */
export interface RiskFactor {
  name: string;
  score: number;        // 0-100
  weight: number;       // 0-1, sum of all weights = 1
  category?: string;
  description?: string;
}

/**
 * Overall risk rating
 */
export interface RiskRating {
  overall: 'low' | 'medium' | 'high' | 'critical';
  score: number;        // 0-100
  factors: RiskFactor[];
  calculatedAt?: string;
  calculatedBy?: string;
  overrideReason?: string;
  overriddenBy?: string;
  overriddenAt?: string;
}

// ============================================================================
// MAIN CLIENT COMPLIANCE DATA
// ============================================================================

/**
 * Main ClientComplianceData interface
 * Single source of truth for all compliance data
 */
export interface ClientComplianceData {
  // Client identifier
  clientId: string;

  // Shared data sections
  personal: SharedPersonalData;
  contact: SharedContactData;
  employment: SharedEmploymentData;
  financial: SharedFinancialData;
  tax: SharedTaxData;
  pep: SharedPEPData;

  // TASK-261: New shared data sections
  identification?: SharedIdentificationData;
  personalBackground?: SharedPersonalBackgroundData;
  wealthSummary?: SharedWealthSummaryData;
  assetBreakdown?: SharedAssetBreakdownData;
  additionalWealthInfo?: SharedAdditionalWealthInfoData;
  incomeExpectations?: SharedIncomeExpectationsData;
  // TASK-322: Income summary (top-level fields)
  incomeSummary?: SharedIncomeSummaryData;
  wealthSources?: SharedWealthSourceData[];
  incomeSources?: SharedIncomeSourceData[];
  businessActivities?: SharedBusinessActivityData[];
  // TASK-321: Adverse media screening data
  adverseMedia?: SharedAdverseMediaData;

  // Beneficial owners (array for multiple owners)
  beneficialOwners: BeneficialOwnerData[];

  // Form statuses
  forms: {
    kyc?: FormStatus;
    riskProfile?: FormStatus;
    clientProfile?: FormStatus;
    formA?: FormStatus;
  };

  // Screening results
  screening: {
    pep?: ScreeningStatus;
    sanctions?: ScreeningStatus;
    adverseMedia?: ScreeningStatus;
  };

  // Risk rating
  riskRating?: RiskRating;

  // Form-specific extensions (data that doesn't fit shared model)
  formExtensions?: {
    kyc?: Record<string, unknown>;
    riskProfile?: Record<string, unknown>;
    clientProfile?: Record<string, unknown>;
    formA?: Record<string, unknown>;
  };

  // Metadata
  metadata: {
    createdAt: string;
    createdBy?: string;
    updatedAt: string;
    updatedBy?: string;
    version: number;
  };
}

// ============================================================================
// FORM REGISTRY TYPES
// ============================================================================

/**
 * Form types supported by the system
 */
export type FormType = 'kyc' | 'riskProfile' | 'clientProfile' | 'formA';

/**
 * Form configuration for registry
 * TASK-231: Updated mapToShared to include DataSource for field tracking
 */
export interface FormConfig<TFormData = unknown> {
  id: FormType;
  name: string;
  description: string;
  version: string;
  requiredSections: (keyof ClientComplianceData)[];
  mapFromShared: (data: ClientComplianceData) => TFormData;
  mapToShared: (formData: TFormData, source: DataSource) => Partial<ClientComplianceData>;
  validationSchema?: unknown;
  extractionSchemaName?: string;
}

// ============================================================================
// FIELD SECTION MAPPING
// ============================================================================

/**
 * Canonical field names mapped to their sections
 * Used for automatic field routing during ingestion
 */
export const FIELD_TO_SECTION: Record<string, keyof ClientComplianceData> = {
  // Personal fields
  firstName: 'personal',
  middleName: 'personal',
  lastName: 'personal',
  dateOfBirth: 'personal',
  placeOfBirth: 'personal',
  gender: 'personal',
  maritalStatus: 'personal',
  nationalities: 'personal',
  languages: 'personal',
  passportNumber: 'personal',
  nationalId: 'personal',

  // Contact fields
  phonePrimary: 'contact',
  phoneSecondary: 'contact',
  emailPrimary: 'contact',
  emailSecondary: 'contact',
  residentialAddress: 'contact',
  mailingAddress: 'contact',
  preferredContactMethod: 'contact',

  // Employment fields
  employmentStatus: 'employment',
  occupation: 'employment',
  employerName: 'employment',
  employmentIndustry: 'employment',
  yearsEmployed: 'employment',
  workAddress: 'employment',

  // Financial fields
  totalAssets: 'financial',
  liquidAssets: 'financial',
  annualIncome: 'financial',
  totalLiabilities: 'financial',
  netWorth: 'financial',
  sourceOfWealth: 'financial',
  primaryWealthSource: 'financial',
  assetCurrency: 'financial',

  // Tax fields
  taxResidence: 'tax',
  taxId: 'tax',
  usPerson: 'tax',
  fatcaStatus: 'tax',
  taxJurisdictions: 'tax',

  // PEP fields
  isPep: 'pep',
  isRca: 'pep',
  pepType: 'pep',
  pepPosition: 'pep',
  pepCountry: 'pep',
  pepStartDate: 'pep',
  pepEndDate: 'pep',
  rcaRelationship: 'pep',
  rcaPepName: 'pep',
};

/**
 * Get the section for a canonical field name
 */
export function getFieldSection(fieldName: string): keyof ClientComplianceData | undefined {
  return FIELD_TO_SECTION[fieldName];
}

// ============================================================================
// SOURCE PRIORITY
// ============================================================================

/**
 * Priority order for conflict resolution (lower = higher priority)
 * manual+verified > manual > api > document > transcription > inherited
 */
const SOURCE_PRIORITY: Record<DataSourceType, number> = {
  manual: 1,
  api: 2,
  document: 3,
  transcription: 4,
  inherited: 5,
};

/**
 * Get priority for a data source (lower number = higher priority)
 */
export function getSourcePriority(source: DataSource): number {
  return SOURCE_PRIORITY[source.type] ?? 99;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Create a new DataSource object
 */
export function createDataSource(
  type: DataSourceType,
  sourceId: string,
  sourceName: string,
  confidence: number,
  metadata?: Record<string, unknown>,
  extractedBy?: string
): DataSource {
  return {
    type,
    sourceId,
    sourceName,
    confidence,
    extractedAt: new Date().toISOString(),
    extractedBy,
    metadata,
  };
}

/**
 * Create a new TrackedField object
 */
export function createTrackedField<T>(
  value: T,
  source: DataSource,
  existingHistory?: DataSource[]
): TrackedField<T> {
  return {
    value,
    confidence: source.confidence,
    source,
    history: existingHistory || [],
    modifiedAt: new Date().toISOString(),
  };
}

/**
 * Unwrap a TrackedField to get its raw value
 * Works with both TrackedField<T> and raw T values for backward compatibility
 */
export function unwrap<T>(field: TrackedField<T> | T | undefined | null): T | undefined {
  if (field === undefined || field === null) {
    return undefined;
  }
  if (isTrackedField(field)) {
    return field.value;
  }
  return field as T;
}

/**
 * Type guard to check if a value is a TrackedField
 */
export function isTrackedField<T>(value: unknown): value is TrackedField<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'source' in value &&
    'confidence' in value &&
    typeof (value as TrackedField<T>).confidence === 'number'
  );
}

/**
 * Wrap a legacy (non-tracked) value with an inherited source
 * Used for backward compatibility with existing data
 */
export function wrapLegacy<T>(value: T, clientId: string): TrackedField<T> {
  return createTrackedField(
    value,
    createDataSource(
      'inherited',
      `legacy_${clientId}`,
      'Legacy Data',
      1.0
    )
  );
}

/**
 * Resolve conflict between existing and incoming tracked fields
 * Returns the winning field with updated history
 */
export function resolveConflict<T>(
  existing: TrackedField<T>,
  incoming: TrackedField<T>
): TrackedField<T> {
  // 1. Verified data is sacred - never overwrite
  if (existing.isVerified && !incoming.isVerified) {
    return {
      ...existing,
      history: [...(existing.history || []), incoming.source],
    };
  }

  // 2. If incoming is verified, it wins
  if (incoming.isVerified && !existing.isVerified) {
    return {
      ...incoming,
      history: [...(existing.history || []), existing.source],
    };
  }

  // 3. Apply priority rules
  const existingPriority = getSourcePriority(existing.source);
  const incomingPriority = getSourcePriority(incoming.source);

  if (incomingPriority < existingPriority) {
    return {
      ...incoming,
      history: [...(existing.history || []), existing.source],
    };
  }

  if (incomingPriority > existingPriority) {
    return {
      ...existing,
      history: [...(existing.history || []), incoming.source],
    };
  }

  // 4. Same priority: prefer newer
  if (incoming.source.extractedAt > existing.source.extractedAt) {
    return {
      ...incoming,
      history: [...(existing.history || []), existing.source],
    };
  }

  // 5. Keep existing if same priority and not newer
  return {
    ...existing,
    history: [...(existing.history || []), incoming.source],
  };
}

/**
 * Create empty ClientComplianceData for a new client
 */
export function createEmptyComplianceData(clientId: string): ClientComplianceData {
  const now = new Date().toISOString();
  return {
    clientId,
    personal: {},
    contact: {},
    employment: {},
    financial: {},
    tax: {},
    pep: {},
    beneficialOwners: [],
    forms: {},
    screening: {},
    metadata: {
      createdAt: now,
      updatedAt: now,
      version: 1,
    },
  };
}

/**
 * Type guard for ClientComplianceData
 */
export function isClientComplianceData(data: unknown): data is ClientComplianceData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'clientId' in data &&
    'personal' in data &&
    'contact' in data &&
    'metadata' in data
  );
}
